import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";
import FileUploadButton from "@/components/admin/FileUploadButton";
import { resolveGoogleLink, GOOGLE_HINT } from "@/components/admin/googleLink";
import { DEFAULT_TEXTS } from "@/constants/defaultTexts";

const inputClass = "w-full rounded-lg border border-stone-300 bg-[#FAF6F0] p-3 text-sm focus:border-[#B83227] focus:outline-none";
const labelClass = "mb-1.5 block text-xs font-bold uppercase text-stone-700";

// Textes regroupés par page du site, dans l'ordre d'affichage (du haut vers le bas)
const TEXT_GROUPS = [
  {
    title: "Menu (en haut de toutes les pages)",
    fields: [{ key: "nav_cta_label", label: "Bouton rouge du menu (ex : Réserver le groupe)" }],
  },
  {
    title: "Accueil — grande photo de couverture",
    fields: [{ key: "home_hero_caption", label: "Texte de présentation sous la photo (vide = masqué)" }],
  },
  {
    title: "Accueil — galerie de photos",
    fields: [
      { key: "home_gallery_label", label: "Petit titre rouge" },
      { key: "home_gallery_title", label: "Titre" },
      { key: "home_gallery_text", label: "Texte" },
      { key: "home_gallery_link", label: "Texte du lien vers la galerie" },
    ],
  },
  {
    title: "Accueil — formules scéniques",
    fields: [
      { key: "home_prestas_title", label: "Titre" },
      { key: "home_prestas_text", label: "Texte" },
      { key: "home_prestas_card_link", label: "Lien de réservation sur les cartes" },
    ],
  },
  {
    title: "Accueil — section « L'Esprit des Sonneurs »",
    fields: [
      { key: "home_ambiance_label", label: "Petit titre rouge" },
      { key: "home_ambiance_title", label: "Titre" },
      { key: "home_ambiance_box_title", label: "Encadré foncé — titre" },
      { key: "home_ambiance_box_text", label: "Encadré foncé — texte" },
      { key: "home_ambiance_image_caption", label: "Titre affiché sur l'image" },
      { key: "home_ambiance_image_sub", label: "Sous-titre affiché sur l'image" },
    ],
  },
  {
    title: "Accueil — agenda",
    fields: [
      { key: "agenda_label", label: "Petit titre rouge" },
      { key: "agenda_title", label: "Titre" },
      { key: "agenda_invite", label: "Texte du lien d'invitation" },
    ],
  },
  {
    title: "Accueil — actualités",
    fields: [
      { key: "home_news_label", label: "Petit titre rouge" },
      { key: "home_news_title", label: "Titre" },
      { key: "home_news_badge", label: "Badge rouge sur les cartes" },
      { key: "home_news_link", label: "Texte du lien « toutes les actualités »" },
    ],
  },
  {
    title: "Accueil — appel final (tout en bas)",
    fields: [
      { key: "home_cta_title", label: "Titre" },
      { key: "home_cta_text", label: "Texte" },
      { key: "home_cta_button", label: "Texte du bouton" },
    ],
  },
  {
    title: "Page Prestations",
    fields: [
      { key: "page_prestations_label", label: "Petit titre rouge" },
      { key: "page_prestations_title", label: "Titre de la page" },
      { key: "page_prestations_intro", label: "Introduction" },
      { key: "page_prestations_card_link", label: "Lien de réservation sur les cartes" },
      { key: "prestas_box_title", label: "Encadré final — titre" },
      { key: "prestas_box_text", label: "Encadré final — texte" },
      { key: "prestas_box_button", label: "Encadré final — bouton" },
    ],
  },
  {
    title: "Page Stages",
    fields: [
      { key: "page_stages_label", label: "Petit titre rouge" },
      { key: "page_stages_title", label: "Titre de la page" },
      { key: "page_stages_intro", label: "Introduction" },
      { key: "stages_organizing_title", label: "Bandeau « en cours d'organisation » — titre" },
      { key: "stages_organizing_text", label: "Bandeau « en cours d'organisation » — texte" },
      { key: "stages_form_title", label: "Titre du formulaire d'inscription" },
    ],
  },
  {
    title: "Page Galerie",
    fields: [
      { key: "page_galerie_label", label: "Petit titre rouge" },
      { key: "page_galerie_title", label: "Titre de la page" },
      { key: "page_galerie_intro", label: "Introduction" },
      { key: "galerie_audio_link", label: "Bouton rouge vers la page Audio" },
    ],
  },
  {
    title: "Page Audio",
    fields: [
      { key: "page_audio_label", label: "Petit titre rouge" },
      { key: "page_audio_title", label: "Titre de la page" },
      { key: "page_audio_intro", label: "Introduction" },
      { key: "audio_hint", label: "Explication sous la liste des enregistrements" },
    ],
  },
  {
    title: "Page Actualités",
    fields: [
      { key: "page_actualites_label", label: "Petit titre rouge" },
      { key: "page_actualites_title", label: "Titre de la page" },
      { key: "page_actualites_intro", label: "Introduction" },
    ],
  },
  {
    title: "Page Association",
    fields: [
      { key: "page_association_label", label: "Petit titre rouge" },
      { key: "page_association_title", label: "Titre de la page" },
      { key: "page_association_intro", label: "Introduction" },
    ],
  },
  {
    title: "Page Contact",
    fields: [
      { key: "page_contact_label", label: "Petit titre rouge" },
      { key: "page_contact_title", label: "Titre de la page" },
      { key: "page_contact_intro", label: "Introduction" },
    ],
  },
];

function Section({ title, children }) {
  return (
    <div className="space-y-4 rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
      <h3 className="font-display text-lg font-bold uppercase text-[#1E1B18]">{title}</h3>
      {children}
    </div>
  );
}

export default function PresentationManager() {
  const { refresh } = useSite();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch(() => {});
  }, []);

  if (!form) return <p className="text-sm text-stone-500">Chargement...</p>;

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const setText = (key) => (e) => setForm({ ...form, texts: { ...(form.texts || {}), [key]: e.target.value } });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", {
        site_title: form.site_title,
        site_tagline: form.site_tagline,
        hero_title: form.hero_title,
        hero_image_url: form.hero_image_url,
        ambiance_image_url: form.ambiance_image_url,
        logo_url: form.logo_url,
        presentation_title: form.presentation_title,
        presentation_text: form.presentation_text,
        region_text: form.region_text,
        contact_email: form.contact_email,
        notification_email: form.notification_email,
        footer_text: form.footer_text,
        texts: form.texts || {},
      });
      setForm(data);
      refresh();
      toast.success("Réglages enregistrés et visibles sur le site");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="presentation-manager">
      <form onSubmit={submit} className="grid max-w-4xl grid-cols-1 gap-6">
        <Section title="Identité du site">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Nom du groupe (titre du site)</label>
              <input data-testid="settings-site-title-input" className={inputClass} value={form.site_title || ""} onChange={set("site_title")} />
            </div>
            <div>
              <label className={labelClass}>Slogan (sous le nom, en rouge)</label>
              <input data-testid="settings-tagline-input" className={inputClass} value={form.site_tagline || ""} onChange={set("site_tagline")} />
              <p className="mt-1 text-xs text-stone-500">Affiché sur une seule ligne : sa taille s'adapte à l'écran, mais un slogan court reste plus lisible.</p>
            </div>
          </div>
          <div>
            <label className={labelClass}>Logo</label>
            <div className="flex items-center gap-4">
              <img src={form.logo_url || "/logo-sonneurs.jpg"} alt="Logo actuel" className="h-16 w-16 rounded-full object-cover ring-1 ring-stone-200" />
              <div className="flex-1">
                <input data-testid="settings-logo-input" className={inputClass} placeholder="https://... (laisser vide = logo par défaut)" value={form.logo_url || ""} onChange={set("logo_url")} onBlur={(e) => resolveGoogleLink(e.target.value, (u) => setForm((f) => ({ ...f, logo_url: u })))} />
                <div className="mt-2">
                  <FileUploadButton testId="settings-logo-upload" accept="image/*" label="Téléverser un autre logo" onUploaded={(url) => setForm((f) => ({ ...f, logo_url: url }))} />
                </div>
              </div>
            </div>
          </div>
        </Section>

        <Section title="Page d'accueil">
          <div>
            <label className={labelClass}>Grand titre d'accueil (affiché sur une seule ligne, en haut de la photo)</label>
            <input data-testid="settings-hero-title-input" className={inputClass} value={form.hero_title || ""} onChange={set("hero_title")} />
          </div>
          <div>
            <label className={labelClass}>Grande image d'accueil</label>
            <input data-testid="settings-hero-input" className={inputClass} placeholder="https://..." value={form.hero_image_url || ""} onChange={set("hero_image_url")} onBlur={(e) => resolveGoogleLink(e.target.value, (u) => setForm((f) => ({ ...f, hero_image_url: u })))} />
            <p className="mt-1 text-xs text-stone-500">{GOOGLE_HINT}</p>
            <div className="mt-2">
              <FileUploadButton testId="settings-hero-upload" accept="image/*" label="Ou téléverser une photo du groupe" onUploaded={(url) => setForm((f) => ({ ...f, hero_image_url: url }))} />
            </div>
          </div>
          <div>
            <label className={labelClass}>Image de la section « L'Esprit des Sonneurs »</label>
            <input data-testid="settings-ambiance-input" className={inputClass} placeholder="https://..." value={form.ambiance_image_url || ""} onChange={set("ambiance_image_url")} onBlur={(e) => resolveGoogleLink(e.target.value, (u) => setForm((f) => ({ ...f, ambiance_image_url: u })))} />
            <div className="mt-2">
              <FileUploadButton testId="settings-ambiance-upload" accept="image/*" label="Ou téléverser une image" onUploaded={(url) => setForm((f) => ({ ...f, ambiance_image_url: url }))} />
            </div>
          </div>
          <div>
            <label className={labelClass}>Texte de présentation</label>
            <textarea data-testid="settings-presentation-input" rows="10" className={inputClass} value={form.presentation_text || ""} onChange={set("presentation_text")} />
            <p className="mt-1 text-xs text-stone-500">
              Mise en forme possible : **gras**, *italique*, retour à la ligne, et une ligne vide pour commencer un nouveau paragraphe. Le texte est affiché justifié sur le site.
            </p>
          </div>
        </Section>

        <Section title="Coordonnées & alertes">
          <div>
            <label className={labelClass}>Zone d'intervention</label>
            <input data-testid="settings-region-input" className={inputClass} value={form.region_text || ""} onChange={set("region_text")} />
          </div>
          <div>
            <label className={labelClass}>E-mail de contact affiché (facultatif)</label>
            <input data-testid="settings-email-input" type="email" className={inputClass} value={form.contact_email || ""} onChange={set("contact_email")} />
          </div>
          <div>
            <label className={labelClass}>E-mail qui reçoit les alertes de nouvelles demandes</label>
            <input data-testid="settings-notification-email-input" type="email" className={inputClass} placeholder="Ex : les.sonneurs@orange.fr" value={form.notification_email || ""} onChange={set("notification_email")} />
            <p className="mt-1 text-xs text-stone-500">
              Dès qu'un visiteur envoie le formulaire de contact, un e-mail récapitulatif part vers cette adresse (vous pouvez répondre directement au visiteur depuis votre boîte mail). Laissez vide pour désactiver les alertes.
            </p>
          </div>
          <div>
            <label className={labelClass}>Texte du pied de page</label>
            <input data-testid="settings-footer-input" className={inputClass} value={form.footer_text || ""} onChange={set("footer_text")} />
          </div>
        </Section>

        <Section title="Textes du site, page par page">
          <p className="text-xs text-stone-500">
            Les textes sont regroupés par page du site, dans l'ordre où ils apparaissent (du haut vers le bas). Les champs sont pré-remplis avec les textes actuellement affichés : modifiez-les puis cliquez sur « Enregistrer les réglages ». Videz un champ pour revenir au texte par défaut.
          </p>
          {TEXT_GROUPS.map((g) => (
            <div key={g.title} className="space-y-4 border-t-2 border-stone-100 pt-5 first:border-0 first:pt-0">
              <p className="font-display text-sm font-bold uppercase tracking-wide text-[#B83227]">{g.title}</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {g.fields.map((f) => (
                  <div key={f.key}>
                    <label className={labelClass}>{f.label}</label>
                    <input
                      data-testid={`text-${f.key}`}
                      className={inputClass}
                      value={(form.texts || {})[f.key] ?? DEFAULT_TEXTS[f.key] ?? ""}
                      onChange={setText(f.key)}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Section>

        <Section title="Mentions légales & confidentialité">
          <p className="text-xs leading-relaxed text-stone-500">
            Ces textes s'affichent sur la page « Mentions légales » (lien dans le menu et en pied de page). Mise en forme possible : **gras**, *italique*,
            ligne vide = nouveau paragraphe. Écrivez {"{president}"} pour afficher automatiquement le nom du président renseigné dans l'onglet Association.
          </p>
          <div>
            <label className={labelClass}>Mentions légales (identité de l'association, hébergeur...)</label>
            <textarea
              data-testid="text-legal_notice_text"
              rows="9"
              className={inputClass}
              value={(form.texts || {}).legal_notice_text ?? DEFAULT_TEXTS.legal_notice_text ?? ""}
              onChange={setText("legal_notice_text")}
            />
          </div>
          <div>
            <label className={labelClass}>Protection des données personnelles</label>
            <textarea
              data-testid="text-legal_privacy_text"
              rows="9"
              className={inputClass}
              value={(form.texts || {}).legal_privacy_text ?? DEFAULT_TEXTS.legal_privacy_text ?? ""}
              onChange={setText("legal_privacy_text")}
            />
          </div>
        </Section>

        <button type="submit" data-testid="settings-save-button" disabled={saving} className="w-fit rounded bg-[#B83227] px-8 py-3 text-xs font-bold uppercase tracking-wider text-white hover:bg-[#96281E] disabled:opacity-60">
          {saving ? "Enregistrement..." : "Enregistrer les réglages"}
        </button>
      </form>
    </div>
  );
}
